import { ZIndexes } from "@common/constants";
import { LayerContainer } from "@common/utils/layer";
import { type Hitbox } from "@common/utils/hitbox";
import { type SkyName } from "@common/utils/mapWeather";
import { FloorNames } from "@common/utils/terrain";
import { Vec } from "@common/utils/vector";
import { Graphics, Sprite, Texture } from "pixi.js";
import { PIXI_SCALE } from "../utils/constants";
import { drawGroundGraphics } from "../utils/pixi";
import { sampleNoise } from "../war/warNoise";
import { type AtlasRead, atlasRead } from "./atlasReadLogic";
import { AtlasReads, canvasWith, readHere } from "./atlasReads";
import { CameraManager } from "./cameraManager";
import { MapManager } from "./mapManager";
import { CAP, capSize, type CapKind, snowCapPixels } from "./snowCapLogic";

/*
    The snow lying on things (0.98.5, owner decision, 1 November 2026: "on snow mode, add abit of snow to the top of
    treees, to roofs, etc, and the river should be freezed, like an iced water surface"):

     - caps of snow on the tops of things outdoors: the trees' crowns, the roofs, the rocks, the bushes, the crates and
       barrels, the cars, whatever stands under the sky. Each picture's cap is made once from the picture itself (its
       shape, eroded a little in from its edge so the thing's sides still show, under patches of snow laid by noise,
       lit from the top left) and kept; a thing shows its cap as a child of its own picture, so it turns, fades and
       goes with it. Nothing indoors gets one (an obstacle under a building's roof), nor anything that lies flat on
       the ground, nor a picture drawn in a colour of its own (a tent's: a child takes its parent's tint, and tinted
       snow is no snow). The caps are baked a few a frame from the atlas pages themselves (the pictures as the browser
       holds them, never read back from the graphics card: on WebGPU that read is a copy of the whole page, and on
       any card a stall), each picture's frame cut out, turned and placed as the atlas keeps it; a thing stands bare
       for the frames until its cap is ready. 0.98.8: baked in the bakes' worker (managers/atlasReads.ts), off the
       frames (a picture drawn off a page the browser had let go of made it decode the whole page, and the game froze
       for it), the cap's sums in managers/snowCapLogic.ts; here without a worker, as before
     - the ice over a building's water (Pool Day's pool, its hot tub): a pale sheet with its cracks and frost, drawn
       over the floor's picture, among the ground floor's things (under whoever walks across it). The rivers and the
       sea freeze in the ground itself: the shaders' (war/warShaders.ts) and the plain look's colours (SNOW_PALETTE);
       the floor under a player there is ice (utils/terrain.ts, the map's `waterType`: walked on, slid on, no splash)
*/

/** the bakes sent to the worker and not back yet, at most (0.98.8) */
const IN_FLIGHT = 4;

/** the ice over a building's water: its colour, its cracks' */
const ICE = Object.freeze({ sheet: 0xcfdde6, sheetAlpha: 0.88, crack: 0x5f7889, frost: 0xf4f8fa });

/** a cap once made: its texture, and the picture's own size it's stretched to */
interface Cap {
    readonly texture: Texture
    readonly width: number
    readonly height: number
}

type Kind = CapKind;

/** a cap waiting to be baked: its picture, its kind, its tries (`here`: the worker couldn't, the page bakes it: 0.98.8) */
interface Job { texture: Texture, kind: Kind, tries: number, here?: boolean }

/** a picture's sprite, with the cap it wants (by its key) */
type Capped = Sprite & { __snowWant?: string };
type Overlay = Sprite & { __snowKey?: string };

/** the atlas page's picture, as the browser can draw it (an image, a bitmap or a canvas) */
type Drawable = CanvasImageSource & { readonly width: number, readonly height: number };
const drawable = (resource: unknown): Drawable | undefined => {
    if (
        (typeof ImageBitmap !== "undefined" && resource instanceof ImageBitmap)
        || (typeof HTMLImageElement !== "undefined" && resource instanceof HTMLImageElement)
        || (typeof HTMLCanvasElement !== "undefined" && resource instanceof HTMLCanvasElement)
        || (typeof OffscreenCanvas !== "undefined" && resource instanceof OffscreenCanvas)
    ) return resource as Drawable;
    return undefined;
};

class SnowLookClass {
    private _sky: SkyName = "clear";
    /** the caps baked, by their key (`null`: this picture takes none) */
    private readonly _caps = new Map<string, Cap | null>();
    /** the keys waiting to be baked, in turn, with the picture each is baked from */
    private readonly _queue: string[] = [];
    private readonly _toBake = new Map<string, Job>();
    /** the caps baked since the last frame looked (0.98.8: the worker's come back between frames) */
    private _baked = new Set<string>();
    /** the page the last bake sent to the worker was read off; each reset's own number (a bake from before one is dropped) */
    private _lastPage?: unknown;
    private _generation = 0;
    /** the sprites waiting for their cap */
    private _waiting: Capped[] = [];
    private _ice?: Graphics;
    private _seed = 0;
    /** the buildings' ceilings by floor, placed (what's under one gets no snow) */
    private readonly _ceilings = new Map<number, Hitbox[]>();

    /** snow this match */
    get active(): boolean { return this._sky === "snow"; }

    /** the map packet came, under this sky (before the map and its things are made) */
    begin(sky: SkyName): void {
        this.reset();
        this._sky = sky;
        this._seed = Math.floor(Math.random() * 1000);
    }

    /** the map's terrain is built: the ice over the buildings' water (Pool Day) */
    mapReady(): void {
        if (!this.active) return;
        this._ceilings.clear();
        const ice = this._ice = new Graphics();
        // (among the ground floor's things: over the floors' pictures, under the decals, the things and the players on it)
        ice.zIndex = ZIndexes.BuildingsFloor + 0.5;
        ice.eventMode = "none";
        let drawn = 0;
        for (const building of MapManager.objects) {
            if (!building.isBuilding) continue;
            for (const floor of building.definition.floors ?? []) {
                if (floor.type !== FloorNames.Water || (floor.layer ?? building.layer ?? 0) !== 0) continue;
                const hitbox = floor.hitbox.transform(building.position, 1, building.orientation);
                ice.beginPath();
                drawGroundGraphics(hitbox, ice);
                ice.closePath();
                ice.fill({ color: ICE.sheet, alpha: ICE.sheetAlpha });
                ice.beginPath();
                this._cracks(ice, hitbox.toRectangle(), drawn++);
                ice.beginPath();
            }
        }
        if (drawn === 0) {
            ice.destroy();
            this._ice = undefined;
            return;
        }
        CameraManager.layerContainers[LayerContainer.Ground].addChild(ice);
    }

    /** the cracks and the frost over a sheet of ice: a few jagged lines across it, pale patches here and there */
    private _cracks(ice: Graphics, rect: { readonly min: { x: number, y: number }, readonly max: { x: number, y: number } }, salt: number): void {
        const w = (rect.max.x - rect.min.x), h = (rect.max.y - rect.min.y);
        const hash = (a: number, b: number): number => sampleNoise((a + salt * 0.37 + this._seed * 0.013) * 0.113, (b + salt * 0.71) * 0.097, 3);
        const lines = Math.max(2, Math.round(Math.sqrt(w * h) / 9));
        ice.setStrokeStyle({ color: ICE.crack, alpha: 0.5, width: 0.09 * PIXI_SCALE, cap: "round", join: "round" });
        for (let i = 0; i < lines; i++) {
            let x = rect.min.x + hash(i, 1) * w, y = rect.min.y + hash(i, 2) * h;
            let angle = hash(i, 3) * Math.PI * 2;
            ice.moveTo(x * PIXI_SCALE, y * PIXI_SCALE);
            const steps = 3 + Math.floor(hash(i, 4) * 4);
            for (let k = 0; k < steps; k++) {
                angle += (hash(i, 10 + k) - 0.5) * 1.6;
                const length = 2 + hash(i, 20 + k) * 5;
                x = Math.min(rect.max.x, Math.max(rect.min.x, x + Math.cos(angle) * length));
                y = Math.min(rect.max.y, Math.max(rect.min.y, y + Math.sin(angle) * length));
                ice.lineTo(x * PIXI_SCALE, y * PIXI_SCALE);
            }
        }
        ice.stroke();
        ice.beginPath();
        const patches = Math.max(2, Math.round(Math.sqrt(w * h) / 7));
        for (let i = 0; i < patches; i++) {
            const x = rect.min.x + hash(i, 30) * w, y = rect.min.y + hash(i, 31) * h;
            ice.circle(x * PIXI_SCALE, y * PIXI_SCALE, (1.2 + hash(i, 32) * 2.6) * PIXI_SCALE);
        }
        ice.fill({ color: ICE.frost, alpha: 0.22 });
    }

    /**
     * a thing's picture gets its cap of snow (a child of the picture's sprite, kept with it): `kind` "roof" for a
     * building's roof (snow over most of it), "thing" for an obstacle (its top). Nothing when the sky isn't snow, the
     * picture is too small to carry any, or it's drawn tinted. The cap comes a few frames on when its picture's hasn't
     * been baked yet
     */
    cap(sprite: Sprite, frame: string, kind: Kind): void {
        if (!this.active) {
            this.uncap(sprite);
            return;
        }
        const key = `${kind}:${frame}`;
        const wanted = sprite as Capped;
        if (wanted.__snowWant === key) return;
        this.uncap(sprite);
        wanted.__snowWant = key;
        const known = this._caps.get(key);
        if (known === null) return;
        if (known !== undefined) {
            this._attach(wanted, key, known);
            return;
        }
        if (!this._toBake.has(key)) {
            this._toBake.set(key, { texture: sprite.texture, kind, tries: 0 });
            this._queue.push(key);
        }
        this._waiting.push(wanted);
    }

    /** its cap gone (the thing broken, the roof torn, the sky not snow) */
    uncap(sprite: Sprite): void {
        (sprite as Capped).__snowWant = undefined;
        for (const child of [...sprite.children]) {
            if ((child as Overlay).__snowKey !== undefined) child.destroy();
        }
    }

    private _attach(sprite: Capped, key: string, cap: Cap): void {
        // (a child's colour is its parent's times its own: a tinted picture would wear tinted snow, so it wears none)
        if (sprite.destroyed || sprite.__snowWant !== key || sprite.tint !== 0xffffff) return;
        const overlay = new Sprite(cap.texture) as Overlay;
        overlay.__snowKey = key;
        overlay.anchor.set(0.5);
        overlay.width = cap.width;
        overlay.height = cap.height;
        // (over the picture's middle, whatever its anchor)
        overlay.position.set((0.5 - sprite.anchor.x) * cap.width, (0.5 - sprite.anchor.y) * cap.height);
        overlay.eventMode = "none";
        sprite.addChild(overlay);
    }

    /**
     * every frame: a few of the caps waiting are baked (the ones a picture on screen waits for first, then the rest in
     * turn, within the frame's budget), and the sprites waiting for them get them
     */
    update(): void {
        if (!this.active || (this._queue.length === 0 && this._baked.size === 0)) return;
        const done = this._baked;
        this._baked = new Set();
        const wanted = new Set<string>();
        for (const sprite of this._waiting) {
            if (!sprite.destroyed && sprite.__snowWant !== undefined) wanted.add(sprite.__snowWant);
        }
        const started = performance.now();
        for (let n = 0; this._queue.length > 0 && (n === 0 || performance.now() - started < CAP.budgetMs); n++) {
            // (a picture on screen before one that has left it; among them, one off the page the worker has now)
            let at = this._queue.findIndex(key => wanted.has(key) && this._toBake.get(key)?.texture.source?.resource === this._lastPage);
            if (at < 0) at = this._queue.findIndex(key => wanted.has(key));
            if (at < 0) at = 0;
            const key = this._queue[at];
            const job = this._toBake.get(key);
            // (0.98.8: to the bakes' worker, a few at a time, when it can read the page)
            const page = job?.texture.source?.resource;
            if (job && !job.here && AtlasReads.canRead(page)) {
                if (AtlasReads.busy >= IN_FLIGHT) break;
                this._queue.splice(at, 1);
                this._send(key, job, page);
                continue;
            }
            this._queue.splice(at, 1);
            if (!job) continue;
            let cap: Cap | undefined;
            try {
                cap = this._bake(job.texture, job.kind);
            } catch (e) {
                // (tried again another frame, a few times: a page not there yet; then left bare)
                if (++job.tries < CAP.tries) {
                    this._queue.push(key);
                    continue;
                }
                console.warn("snow: a cap couldn't be baked", key, e);
            }
            this._caps.set(key, cap ?? null);
            this._toBake.delete(key);
            done.add(key);
        }
        if (done.size === 0) return;
        const still: Capped[] = [];
        for (const sprite of this._waiting) {
            const want = sprite.__snowWant;
            if (sprite.destroyed || want === undefined) continue;
            if (!done.has(want)) {
                still.push(sprite);
                continue;
            }
            const cap = this._caps.get(want);
            if (cap) this._attach(sprite, want, cap);
        }
        this._waiting = still;
    }

    /**
     * how a picture is read for its cap (0.98.8: shared by the worker's bake and the page's): its page, and its frame cut
     * out of it (turned back and set in its own frame as the page keeps it: the packer turns and trims pictures), the
     * cap drawn small, each side rounded to whole pixels; none for a picture too small for snow, or not off an atlas page
     */
    private _plan(texture: Texture, kind: Kind): { readonly page: Drawable, readonly read: AtlasRead, readonly width: number, readonly height: number } | undefined {
        const width = texture.orig.width, height = texture.orig.height;
        if (width < 14 || height < 14) return undefined;
        const page = drawable(texture.source.resource);
        if (!page) return undefined;
        // (the packer's turn only, a quarter turn: anything else isn't the atlas's)
        const rotate = texture.rotate;
        if (rotate !== 0 && rotate !== 2) return undefined;
        const size = capSize(kind);
        const k = Math.min(1, size / Math.max(width, height));
        const w = Math.max(4, Math.round(width * k)), h = Math.max(4, Math.round(height * k));
        const read = atlasRead({ frame: texture.frame, trim: texture.trim, rotate, resolution: texture.source.resolution || 1 }, width, height, w, h);
        return { page, read, width, height };
    }

    /**
     * a bake sent to the worker (0.98.8): its cap kept when its pixels come back (dropped if the match was left
     * meanwhile), the sprites waiting for it given it the next frame; when the worker can't, back in the queue to be
     * baked here
     */
    private _send(key: string, job: Job, page: HTMLImageElement): void {
        const plan = this._plan(job.texture, job.kind);
        if (!plan) {
            this._caps.set(key, null);
            this._toBake.delete(key);
            this._baked.add(key);
            return;
        }
        this._lastPage = page;
        const generation = this._generation;
        const { w, h } = plan.read;
        AtlasReads.bake(page, { kind: "snow", read: plan.read, cap: job.kind, seed: this._seed }).then(pixels => {
            if (generation !== this._generation || !this._toBake.has(key)) return;
            const canvas = pixels ? canvasWith(pixels, w, h) : undefined;
            this._caps.set(key, canvas ? { texture: Texture.from(canvas), width: plan.width, height: plan.height } : null);
            this._toBake.delete(key);
            this._baked.add(key);
        }, () => {
            if (generation !== this._generation || !this._toBake.has(key)) return;
            job.here = true;
            this._queue.push(key);
        });
    }

    /**
     * the cap baked here from the picture: its frame cut out of its atlas page (`_plan`), eroded in from its edge, under
     * patches of snow laid by noise, white, bluer in its shadows, lit from the top left (managers/snowCapLogic.ts)
     */
    private _bake(texture: Texture, kind: Kind): Cap | undefined {
        const plan = this._plan(texture, kind);
        if (!plan) return undefined;
        const here = readHere(plan.page, plan.read);
        if (!here) return undefined;
        const { w, h } = plan.read;
        const pixels = snowCapPixels(here.data.data, w, h, kind, this._seed);
        if (!pixels) return undefined;
        const out = here.ctx.createImageData(w, h);
        out.data.set(pixels);
        here.ctx.putImageData(out, 0, 0);
        return { texture: Texture.from(here.canvas), width: plan.width, height: plan.height };
    }

    /** the buildings' ceilings on `layer`, placed, made once (an obstacle asks for each of its pictures) */
    ceilingsOn(layer: number): readonly Hitbox[] {
        let ceilings = this._ceilings.get(layer);
        if (ceilings) return ceilings;
        ceilings = [];
        for (const building of MapManager.objects) {
            if (!building.isBuilding || (building.layer ?? 0) !== layer) continue;
            const definition = building.definition;
            if (!definition.ceilingImages?.length || definition.openAir) continue;
            const ceiling = definition.ceilingHitbox;
            if (!ceiling) continue;
            ceilings.push(ceiling.transform(building.position, 1, building.orientation));
        }
        this._ceilings.set(layer, ceilings);
        return ceilings;
    }

    /** leaving a match (or another sky's map coming): the caps' textures and the ice gone */
    reset(): void {
        this._sky = "clear";
        for (const cap of this._caps.values()) cap?.texture.destroy(true);
        this._caps.clear();
        this._queue.length = 0;
        this._toBake.clear();
        this._baked.clear();
        this._generation++;
        this._waiting = [];
        this._ceilings.clear();
        this._ice?.destroy();
        this._ice = undefined;
    }
}

export const SnowLook = new SnowLookClass();

/** whether a spot on `layer` is under a building's roof (nothing there gets snow): a building's on that floor, not a deck's */
export function underRoof(position: { x: number, y: number }, layer: number): boolean {
    const point = Vec(position.x, position.y);
    for (const ceiling of SnowLook.ceilingsOn(layer)) {
        if (ceiling.isPointInside(point)) return true;
    }
    return false;
}
